/**
 * FI (Finanzas) — "Costos" sub-tab.
 *
 * Owner-only cost entry surface. Everything captured here feeds the
 * Cockpit margin calculations (src/lib/billing/cogs.ts): vendor unit
 * costs resolve COGS per revenue line, overheads get allocated on top,
 * and the group/intragroup map decides which revenue nets out at the
 * consolidated level instead of counting as "external" revenue.
 *
 * Three independent, self-fetching sections:
 *   1. Vendor Cost Rates  (vendor_cost_rates)
 *   2. Overheads Fijos    (overhead_costs)
 *   3. Empresas del Grupo (group_domain_map)
 */
import { useEffect, useState } from 'react'
import { api } from '../../../services/api'
import type { Service } from '../../../services/api'
import type {
  VendorCostRate,
  CostType,
  OverheadCost,
  OverheadCategory,
  CostClass,
  AllocationMethod,
  GroupDomain,
} from './fiTypes'
import { formatCurrency } from './fiFormat'

const KNOWN_VENDORS = ['CrowdStrike', 'JumpCloud', 'Cloudflare', 'SocRadar', 'Drata']

const COST_TYPE_LABELS: Record<CostType, string> = {
  per_user_monthly: 'Por usuario / mes',
  fixed_monthly: 'Fijo mensual',
  one_time_per_unit: 'Único por unidad',
}

const CATEGORY_LABELS: Record<OverheadCategory, string> = {
  payroll: 'Nómina',
  infrastructure: 'Infraestructura',
  tooling: 'Herramientas',
  facilities: 'Instalaciones',
  other: 'Otro',
}

const COST_CLASS_LABELS: Record<CostClass, string> = {
  cogs: 'COGS',
  opex: 'OPEX',
}

const ALLOCATION_LABELS: Record<AllocationMethod, string> = {
  unallocated: 'Sin asignar',
  per_active_user: 'Por usuario activo',
  per_entity_equal: 'Por entidad (igual)',
}

function inputCls() {
  return 'w-full bg-gray-700 text-white px-3 py-2 rounded-lg border border-gray-600 focus:border-jarix-orange focus:outline-none text-sm'
}
function labelCls() {
  return 'block text-xs font-medium text-gray-400 mb-1'
}

export default function FiCostEntry() {
  return (
    <div className="space-y-8">
      <div className="bg-jarix-orange/10 border border-jarix-orange/30 rounded-xl p-4">
        <h2 className="text-white font-semibold mb-1">Costos</h2>
        <p className="text-gray-300 text-sm">
          Estos datos alimentan directamente el margen del{' '}
          <span className="text-jarix-orange font-medium">Cockpit</span>: las
          tarifas de vendor resuelven el COGS de cada línea de ingreso, los
          overheads se asignan encima, y el mapa de dominios del grupo decide
          qué ingreso es intra-grupo (se elimina en la vista consolidada) vs.
          externo. Mientras una tarifa quede en $0, esa parte del margen se
          reporta como incompleta.
        </p>
      </div>

      <VendorRatesSection />
      <OverheadCostsSection />
      <GroupDomainsSection />
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────
// 1. Vendor Cost Rates
// ─────────────────────────────────────────────────────────────────────

interface VendorRateFormState {
  vendorMode: 'known' | 'other'
  vendor: string
  vendorOther: string
  serviceId: string // '' = none
  costType: CostType
  unitCost: string
  currency: string
  effectiveFrom: string
  effectiveTo: string
  notes: string
}

function emptyVendorRateForm(): VendorRateFormState {
  return {
    vendorMode: 'known',
    vendor: KNOWN_VENDORS[0],
    vendorOther: '',
    serviceId: '',
    costType: 'per_user_monthly',
    unitCost: '0',
    currency: 'USD',
    effectiveFrom: new Date().toISOString().slice(0, 10),
    effectiveTo: '',
    notes: '',
  }
}

function vendorRateToForm(r: VendorCostRate): VendorRateFormState {
  const known = KNOWN_VENDORS.includes(r.vendor)
  return {
    vendorMode: known ? 'known' : 'other',
    vendor: known ? r.vendor : KNOWN_VENDORS[0],
    vendorOther: known ? '' : r.vendor,
    serviceId: r.serviceId || '',
    costType: r.costType,
    unitCost: String(r.unitCost),
    currency: r.currency || 'USD',
    effectiveFrom: r.effectiveFrom?.slice(0, 10) || new Date().toISOString().slice(0, 10),
    effectiveTo: r.effectiveTo ? r.effectiveTo.slice(0, 10) : '',
    notes: r.notes || '',
  }
}

function VendorRatesSection() {
  const [rates, setRates] = useState<VendorCostRate[]>([])
  const [services, setServices] = useState<Service[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showForm, setShowForm] = useState(false)
  const [editing, setEditing] = useState<VendorCostRate | null>(null)

  const load = async () => {
    try {
      setLoading(true)
      setError(null)
      const [ratesRes, servicesRes] = await Promise.all([
        api.listVendorCostRates(),
        api.listServices(),
      ])
      if (!ratesRes.success) throw new Error(ratesRes.error || 'Error al cargar tarifas de costo')
      setRates(ratesRes.data?.data || [])
      setServices(servicesRes.data?.services || [])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar tarifas de costo')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [])

  const handleAdd = () => { setEditing(null); setShowForm(true) }
  const handleEdit = (r: VendorCostRate) => { setEditing(r); setShowForm(true) }

  const handleDelete = async (id: string) => {
    if (!confirm('¿Eliminar esta tarifa de costo?')) return
    try {
      const res = await api.deleteVendorCostRate(id)
      if (!res.success) throw new Error(res.error || 'Error al eliminar')
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al eliminar')
    }
  }

  const handleSave = async (form: VendorRateFormState) => {
    const vendor = form.vendorMode === 'known' ? form.vendor : form.vendorOther.trim()
    if (!vendor) { setError('El vendor es requerido'); return }
    const payload = {
      vendor,
      serviceId: form.serviceId || null,
      costType: form.costType,
      unitCost: parseFloat(form.unitCost) || 0,
      currency: form.currency || 'USD',
      effectiveFrom: form.effectiveFrom,
      effectiveTo: form.effectiveTo || null,
      notes: form.notes || null,
    }
    try {
      const res = editing
        ? await api.updateVendorCostRate(editing.id, payload)
        : await api.createVendorCostRate(payload)
      if (!res.success) throw new Error(res.error || 'Error al guardar tarifa')
      await load()
      setShowForm(false)
      setEditing(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al guardar tarifa')
    }
  }

  return (
    <section className="bg-gray-800 rounded-xl border border-gray-700 p-5">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-white font-semibold">Costos por Vendor</h3>
          <p className="text-gray-500 text-xs mt-0.5">
            Tarifa unitaria por proveedor/servicio usada para calcular COGS.
          </p>
        </div>
        <button
          onClick={handleAdd}
          className="bg-jarix-orange hover:bg-jarix-pink text-white text-sm font-medium py-1.5 px-3 rounded-lg transition-colors"
        >
          + Tarifa
        </button>
      </div>

      {error && (
        <div className="bg-red-500/20 border border-red-500 text-red-400 px-3 py-2 rounded-lg mb-4 text-sm">
          {error}
          <button onClick={() => setError(null)} className="float-right">&times;</button>
        </div>
      )}

      {loading ? (
        <p className="text-gray-500 text-sm py-4">Cargando…</p>
      ) : rates.length === 0 ? (
        <p className="text-gray-500 text-sm py-4">Sin tarifas configuradas todavía.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 text-xs uppercase border-b border-gray-700">
                <th className="py-2 pr-4">Vendor</th>
                <th className="py-2 pr-4">Servicio</th>
                <th className="py-2 pr-4">Tipo</th>
                <th className="py-2 pr-4">Costo unitario</th>
                <th className="py-2 pr-4">Vigencia</th>
                <th className="py-2 pr-4"></th>
              </tr>
            </thead>
            <tbody>
              {rates.map((r) => {
                const isPlaceholder = r.unitCost === 0
                return (
                  <tr
                    key={r.id}
                    className={`border-b border-gray-700/50 ${isPlaceholder ? 'bg-amber-500/10' : ''}`}
                  >
                    <td className="py-2.5 pr-4 text-white font-medium">{r.vendor}</td>
                    <td className="py-2.5 pr-4 text-gray-300">{r.serviceName || <span className="text-gray-600">—</span>}</td>
                    <td className="py-2.5 pr-4 text-gray-400">{COST_TYPE_LABELS[r.costType]}</td>
                    <td className="py-2.5 pr-4">
                      <span className={isPlaceholder ? 'text-amber-400 font-medium' : 'text-white'}>
                        {formatCurrency(r.unitCost, r.currency)}
                      </span>
                      {isPlaceholder && (
                        <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 align-middle">
                          EJEMPLO — completar
                        </span>
                      )}
                    </td>
                    <td className="py-2.5 pr-4 text-gray-500 text-xs">
                      {r.effectiveFrom}{r.effectiveTo ? ` → ${r.effectiveTo}` : ' → activo'}
                    </td>
                    <td className="py-2.5 pr-4 text-right whitespace-nowrap">
                      <button onClick={() => handleEdit(r)} className="text-gray-400 hover:text-jarix-orange px-2" title="Editar">✎</button>
                      <button onClick={() => handleDelete(r.id)} className="text-gray-400 hover:text-red-400 px-2" title="Eliminar">✕</button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <VendorRateFormModal
          initial={editing ? vendorRateToForm(editing) : emptyVendorRateForm()}
          services={services}
          isEdit={!!editing}
          onSave={handleSave}
          onClose={() => { setShowForm(false); setEditing(null) }}
        />
      )}
    </section>
  )
}

function VendorRateFormModal({ initial, services, isEdit, onSave, onClose }: {
  initial: VendorRateFormState
  services: Service[]
  isEdit: boolean
  onSave: (form: VendorRateFormState) => void
  onClose: () => void
}) {
  const [form, setForm] = useState<VendorRateFormState>(initial)

  const activeVendor = form.vendorMode === 'known' ? form.vendor : form.vendorOther
  const filteredServices = services.filter((s) => {
    if (!activeVendor) return true
    if (!s.vendor) return false
    return s.vendor.toLowerCase() === activeVendor.toLowerCase()
  })
  const serviceOptions = filteredServices.length > 0 ? filteredServices : services

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSave(form)
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-700">
          <h2 className="text-xl font-semibold text-white">
            {isEdit ? 'Editar Tarifa' : 'Nueva Tarifa de Vendor'}
          </h2>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className={labelCls()}>Vendor *</label>
            <div className="flex gap-2 mb-2">
              <select
                value={form.vendorMode === 'known' ? form.vendor : '__other__'}
                onChange={(e) => {
                  if (e.target.value === '__other__') {
                    setForm({ ...form, vendorMode: 'other' })
                  } else {
                    setForm({ ...form, vendorMode: 'known', vendor: e.target.value })
                  }
                }}
                className={inputCls()}
              >
                {KNOWN_VENDORS.map((v) => <option key={v} value={v}>{v}</option>)}
                <option value="__other__">Otro (especificar)…</option>
              </select>
            </div>
            {form.vendorMode === 'other' && (
              <input
                type="text"
                value={form.vendorOther}
                onChange={(e) => setForm({ ...form, vendorOther: e.target.value })}
                placeholder="Nombre del vendor"
                className={inputCls()}
                required
              />
            )}
          </div>

          <div>
            <label className={labelCls()}>Servicio (opcional)</label>
            <select
              value={form.serviceId}
              onChange={(e) => setForm({ ...form, serviceId: e.target.value })}
              className={inputCls()}
            >
              <option value="">Sin servicio específico</option>
              {serviceOptions.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls()}>Tipo de costo *</label>
              <select
                value={form.costType}
                onChange={(e) => setForm({ ...form, costType: e.target.value as CostType })}
                className={inputCls()}
              >
                {(Object.keys(COST_TYPE_LABELS) as CostType[]).map((k) => (
                  <option key={k} value={k}>{COST_TYPE_LABELS[k]}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls()}>Costo unitario *</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.unitCost}
                onChange={(e) => setForm({ ...form, unitCost: e.target.value })}
                className={inputCls()}
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className={labelCls()}>Moneda</label>
              <input
                type="text"
                value={form.currency}
                onChange={(e) => setForm({ ...form, currency: e.target.value.toUpperCase() })}
                className={inputCls()}
                maxLength={3}
              />
            </div>
            <div>
              <label className={labelCls()}>Vigente desde *</label>
              <input
                type="date"
                value={form.effectiveFrom}
                onChange={(e) => setForm({ ...form, effectiveFrom: e.target.value })}
                className={inputCls()}
                required
              />
            </div>
            <div>
              <label className={labelCls()}>Vigente hasta</label>
              <input
                type="date"
                value={form.effectiveTo}
                onChange={(e) => setForm({ ...form, effectiveTo: e.target.value })}
                className={inputCls()}
              />
            </div>
          </div>

          <div>
            <label className={labelCls()}>Notas</label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className={inputCls()}
              rows={2}
            />
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors">
              Cancelar
            </button>
            <button type="submit" className="flex-1 px-4 py-2 bg-jarix-orange text-white rounded-lg hover:bg-jarix-pink transition-colors">
              {isEdit ? 'Guardar' : 'Crear'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────
// 2. Overheads Fijos
// ─────────────────────────────────────────────────────────────────────

interface OverheadFormState {
  name: string
  category: OverheadCategory
  costClass: CostClass
  monthlyAmount: string
  currency: string
  allocationMethod: AllocationMethod
  effectiveFrom: string
  effectiveTo: string
  isActive: boolean
  notes: string
}

function emptyOverheadForm(): OverheadFormState {
  return {
    name: '',
    category: 'other',
    costClass: 'opex',
    monthlyAmount: '0',
    currency: 'USD',
    allocationMethod: 'unallocated',
    effectiveFrom: new Date().toISOString().slice(0, 10),
    effectiveTo: '',
    isActive: true,
    notes: '',
  }
}

function overheadToForm(o: OverheadCost): OverheadFormState {
  return {
    name: o.name,
    category: o.category,
    costClass: o.costClass,
    monthlyAmount: String(o.monthlyAmount),
    currency: o.currency || 'USD',
    allocationMethod: o.allocationMethod,
    effectiveFrom: o.effectiveFrom?.slice(0, 10) || new Date().toISOString().slice(0, 10),
    effectiveTo: o.effectiveTo ? o.effectiveTo.slice(0, 10) : '',
    isActive: o.isActive,
    notes: o.notes || '',
  }
}

function OverheadCostsSection() {
  const [overheads, setOverheads] = useState<OverheadCost[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showForm, setShowForm] = useState(false)
  const [editing, setEditing] = useState<OverheadCost | null>(null)

  const load = async () => {
    try {
      setLoading(true)
      setError(null)
      const res = await api.listOverheadCosts()
      if (!res.success) throw new Error(res.error || 'Error al cargar overheads')
      setOverheads(res.data?.data || [])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar overheads')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [])

  const handleAdd = () => { setEditing(null); setShowForm(true) }
  const handleEdit = (o: OverheadCost) => { setEditing(o); setShowForm(true) }

  const handleDeactivate = async (id: string) => {
    if (!confirm('¿Desactivar este costo fijo? Dejará de asignarse a partir de hoy.')) return
    try {
      const res = await api.deleteOverheadCost(id)
      if (!res.success) throw new Error(res.error || 'Error al desactivar')
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al desactivar')
    }
  }

  const handleReactivate = async (id: string) => {
    try {
      const res = await api.updateOverheadCost(id, { isActive: true, effectiveTo: null })
      if (!res.success) throw new Error(res.error || 'Error al reactivar')
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al reactivar')
    }
  }

  const handleSave = async (form: OverheadFormState) => {
    if (!form.name.trim()) { setError('El nombre es requerido'); return }
    const payload = {
      name: form.name.trim(),
      category: form.category,
      costClass: form.costClass,
      monthlyAmount: parseFloat(form.monthlyAmount) || 0,
      currency: form.currency || 'USD',
      allocationMethod: form.allocationMethod,
      effectiveFrom: form.effectiveFrom,
      effectiveTo: form.effectiveTo || null,
      isActive: form.isActive,
      notes: form.notes || null,
    }
    try {
      const res = editing
        ? await api.updateOverheadCost(editing.id, payload)
        : await api.createOverheadCost(payload)
      if (!res.success) throw new Error(res.error || 'Error al guardar overhead')
      await load()
      setShowForm(false)
      setEditing(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al guardar overhead')
    }
  }

  return (
    <section className="bg-gray-800 rounded-xl border border-gray-700 p-5">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-white font-semibold">Overheads Fijos</h3>
          <p className="text-gray-500 text-xs mt-0.5">
            Costos fijos mensuales (nómina, infraestructura, herramientas…) asignados según el método elegido.
          </p>
        </div>
        <button
          onClick={handleAdd}
          className="bg-jarix-orange hover:bg-jarix-pink text-white text-sm font-medium py-1.5 px-3 rounded-lg transition-colors"
        >
          + Overhead
        </button>
      </div>

      {error && (
        <div className="bg-red-500/20 border border-red-500 text-red-400 px-3 py-2 rounded-lg mb-4 text-sm">
          {error}
          <button onClick={() => setError(null)} className="float-right">&times;</button>
        </div>
      )}

      {loading ? (
        <p className="text-gray-500 text-sm py-4">Cargando…</p>
      ) : overheads.length === 0 ? (
        <p className="text-gray-500 text-sm py-4">Sin overheads configurados todavía.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 text-xs uppercase border-b border-gray-700">
                <th className="py-2 pr-4">Nombre</th>
                <th className="py-2 pr-4">Categoría</th>
                <th className="py-2 pr-4">Clase</th>
                <th className="py-2 pr-4">Monto mensual</th>
                <th className="py-2 pr-4">Asignación</th>
                <th className="py-2 pr-4">Estado</th>
                <th className="py-2 pr-4"></th>
              </tr>
            </thead>
            <tbody>
              {overheads.map((o) => (
                <tr key={o.id} className={`border-b border-gray-700/50 ${!o.isActive ? 'opacity-50' : ''}`}>
                  <td className="py-2.5 pr-4 text-white font-medium">{o.name}</td>
                  <td className="py-2.5 pr-4 text-gray-300">{CATEGORY_LABELS[o.category]}</td>
                  <td className="py-2.5 pr-4">
                    <span className={`px-1.5 py-0.5 rounded text-xs ${o.costClass === 'cogs' ? 'bg-purple-500/20 text-purple-300' : 'bg-blue-500/20 text-blue-300'}`}>
                      {COST_CLASS_LABELS[o.costClass]}
                    </span>
                  </td>
                  <td className="py-2.5 pr-4 text-white">{formatCurrency(o.monthlyAmount, o.currency)}</td>
                  <td className="py-2.5 pr-4 text-gray-400">{ALLOCATION_LABELS[o.allocationMethod]}</td>
                  <td className="py-2.5 pr-4">
                    {o.isActive ? (
                      <span className="text-xs px-1.5 py-0.5 rounded bg-green-500/20 text-green-400">Activo</span>
                    ) : (
                      <span className="text-xs px-1.5 py-0.5 rounded bg-gray-600/40 text-gray-400">Inactivo</span>
                    )}
                  </td>
                  <td className="py-2.5 pr-4 text-right whitespace-nowrap">
                    <button onClick={() => handleEdit(o)} className="text-gray-400 hover:text-jarix-orange px-2" title="Editar">✎</button>
                    {o.isActive ? (
                      <button onClick={() => handleDeactivate(o.id)} className="text-gray-400 hover:text-red-400 px-2" title="Desactivar">✕</button>
                    ) : (
                      <button onClick={() => handleReactivate(o.id)} className="text-gray-400 hover:text-green-400 px-2" title="Reactivar">↺</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <OverheadFormModal
          initial={editing ? overheadToForm(editing) : emptyOverheadForm()}
          isEdit={!!editing}
          onSave={handleSave}
          onClose={() => { setShowForm(false); setEditing(null) }}
        />
      )}
    </section>
  )
}

function OverheadFormModal({ initial, isEdit, onSave, onClose }: {
  initial: OverheadFormState
  isEdit: boolean
  onSave: (form: OverheadFormState) => void
  onClose: () => void
}) {
  const [form, setForm] = useState<OverheadFormState>(initial)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSave(form)
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-700">
          <h2 className="text-xl font-semibold text-white">
            {isEdit ? 'Editar Overhead' : 'Nuevo Overhead'}
          </h2>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className={labelCls()}>Nombre *</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className={inputCls()}
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls()}>Categoría *</label>
              <select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value as OverheadCategory })}
                className={inputCls()}
              >
                {(Object.keys(CATEGORY_LABELS) as OverheadCategory[]).map((k) => (
                  <option key={k} value={k}>{CATEGORY_LABELS[k]}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls()}>Clase *</label>
              <select
                value={form.costClass}
                onChange={(e) => setForm({ ...form, costClass: e.target.value as CostClass })}
                className={inputCls()}
              >
                {(Object.keys(COST_CLASS_LABELS) as CostClass[]).map((k) => (
                  <option key={k} value={k}>{COST_CLASS_LABELS[k]}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls()}>Monto mensual *</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.monthlyAmount}
                onChange={(e) => setForm({ ...form, monthlyAmount: e.target.value })}
                className={inputCls()}
                required
              />
            </div>
            <div>
              <label className={labelCls()}>Moneda</label>
              <input
                type="text"
                value={form.currency}
                onChange={(e) => setForm({ ...form, currency: e.target.value.toUpperCase() })}
                className={inputCls()}
                maxLength={3}
              />
            </div>
          </div>

          <div>
            <label className={labelCls()}>Método de asignación *</label>
            <select
              value={form.allocationMethod}
              onChange={(e) => setForm({ ...form, allocationMethod: e.target.value as AllocationMethod })}
              className={inputCls()}
            >
              {(Object.keys(ALLOCATION_LABELS) as AllocationMethod[]).map((k) => (
                <option key={k} value={k}>{ALLOCATION_LABELS[k]}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls()}>Vigente desde *</label>
              <input
                type="date"
                value={form.effectiveFrom}
                onChange={(e) => setForm({ ...form, effectiveFrom: e.target.value })}
                className={inputCls()}
                required
              />
            </div>
            <div>
              <label className={labelCls()}>Vigente hasta</label>
              <input
                type="date"
                value={form.effectiveTo}
                onChange={(e) => setForm({ ...form, effectiveTo: e.target.value })}
                className={inputCls()}
              />
            </div>
          </div>

          {isEdit && (
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="overhead-active"
                checked={form.isActive}
                onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-jarix-orange focus:ring-jarix-orange"
              />
              <label htmlFor="overhead-active" className="text-sm text-gray-300">Activo</label>
            </div>
          )}

          <div>
            <label className={labelCls()}>Notas</label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className={inputCls()}
              rows={2}
            />
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors">
              Cancelar
            </button>
            <button type="submit" className="flex-1 px-4 py-2 bg-jarix-orange text-white rounded-lg hover:bg-jarix-pink transition-colors">
              {isEdit ? 'Guardar' : 'Crear'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────
// 3. Empresas del Grupo (group_domain_map)
// ─────────────────────────────────────────────────────────────────────

function GroupDomainsSection() {
  const [domains, setDomains] = useState<GroupDomain[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [editingNameId, setEditingNameId] = useState<string | null>(null)
  const [nameDraft, setNameDraft] = useState('')
  const [savingId, setSavingId] = useState<string | null>(null)

  const load = async () => {
    try {
      setLoading(true)
      setError(null)
      const res = await api.listGroupDomains()
      if (!res.success) throw new Error(res.error || 'Error al cargar dominios del grupo')
      setDomains(res.data?.data || [])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar dominios del grupo')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [])

  const startRename = (d: GroupDomain) => {
    setEditingNameId(d.id)
    setNameDraft(d.subCompanyName)
  }

  const saveRename = async (id: string) => {
    if (!nameDraft.trim()) { setEditingNameId(null); return }
    try {
      setSavingId(id)
      const res = await api.updateGroupDomain(id, { subCompanyName: nameDraft.trim() })
      if (!res.success) throw new Error(res.error || 'Error al renombrar')
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al renombrar')
    } finally {
      setSavingId(null)
      setEditingNameId(null)
    }
  }

  const toggleIntragroup = async (d: GroupDomain) => {
    try {
      setSavingId(d.id)
      const res = await api.updateGroupDomain(d.id, { isIntragroup: !d.isIntragroup })
      if (!res.success) throw new Error(res.error || 'Error al actualizar')
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al actualizar')
    } finally {
      setSavingId(null)
    }
  }

  return (
    <section className="bg-gray-800 rounded-xl border border-gray-700 p-5">
      <div className="mb-4">
        <h3 className="text-white font-semibold">Empresas del Grupo</h3>
        <p className="text-gray-500 text-xs mt-0.5">
          Sub-dominios de correo detectados dentro de cada tenant. Marca cuáles son intra-grupo
          (no cuentan como ingreso externo) y ajusta el nombre visible de la sub-empresa.
        </p>
      </div>

      {error && (
        <div className="bg-red-500/20 border border-red-500 text-red-400 px-3 py-2 rounded-lg mb-4 text-sm">
          {error}
          <button onClick={() => setError(null)} className="float-right">&times;</button>
        </div>
      )}

      {loading ? (
        <p className="text-gray-500 text-sm py-4">Cargando…</p>
      ) : domains.length === 0 ? (
        <p className="text-gray-500 text-sm py-4">No hay dominios detectados todavía.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 text-xs uppercase border-b border-gray-700">
                <th className="py-2 pr-4">Tenant</th>
                <th className="py-2 pr-4">Dominio</th>
                <th className="py-2 pr-4">Sub-empresa</th>
                <th className="py-2 pr-4">Usuarios</th>
                <th className="py-2 pr-4">Intra-grupo</th>
              </tr>
            </thead>
            <tbody>
              {domains.map((d) => (
                <tr key={d.id} className="border-b border-gray-700/50">
                  <td className="py-2.5 pr-4 text-gray-400">{d.parentTenantSlug || '—'}</td>
                  <td className="py-2.5 pr-4 text-gray-300 font-mono text-xs">{d.emailDomain}</td>
                  <td className="py-2.5 pr-4">
                    {editingNameId === d.id ? (
                      <input
                        autoFocus
                        type="text"
                        value={nameDraft}
                        onChange={(e) => setNameDraft(e.target.value)}
                        onBlur={() => saveRename(d.id)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') saveRename(d.id)
                          if (e.key === 'Escape') setEditingNameId(null)
                        }}
                        className="bg-gray-700 text-white px-2 py-1 rounded border border-jarix-orange focus:outline-none text-sm w-40"
                        disabled={savingId === d.id}
                      />
                    ) : (
                      <button
                        onClick={() => startRename(d)}
                        className="text-white hover:text-jarix-orange text-left flex items-center gap-1.5"
                        title="Click para renombrar"
                      >
                        {d.subCompanyName}
                        {d.autoDiscovered && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-300">
                            Auto-detectado
                          </span>
                        )}
                      </button>
                    )}
                  </td>
                  <td className="py-2.5 pr-4 text-gray-400">{d.userCount ?? '—'}</td>
                  <td className="py-2.5 pr-4">
                    <button
                      onClick={() => toggleIntragroup(d)}
                      disabled={savingId === d.id}
                      className={`text-xs px-2 py-1 rounded-full font-medium transition-colors ${
                        d.isIntragroup
                          ? 'bg-purple-500/20 text-purple-300 hover:bg-purple-500/30'
                          : 'bg-gray-600/40 text-gray-400 hover:bg-gray-600/60'
                      }`}
                    >
                      {d.isIntragroup ? 'Sí — intra-grupo' : 'No — externo'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}
